// AdminStudents.js

import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback
} from 'react';
import {
  Box,
  Button,
  Snackbar,
  CircularProgress,
  Alert,
  Tab,
  Typography,
  TextField,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { API_BASE_URL } from 'src/apiConfig';
import { useTranslation } from 'react-i18next';
import { useLocaleText } from 'src/helper/localeText';
import AddStudentDialog from 'src/components/dialogs/student/addStudentDialog';
import ConfirmDeleteDialog from 'src/components/dialogs/delete/ConfirmDeleteDialog';
import { RoleContext } from 'src/context/roleContext';
import { ActiveModesContext } from 'src/context/ActiveModesContext';

import {
  FLAG_FINALE,
  FLAG_REZULTATI,
  FLAG_REZULTATI_SKOLA_G,
  FLAG_ZALBE,
  FLAG_ZABRANA
} from 'src/constants/competitions';
import {
  StyledTabs,
  TabsWrapper
} from '../../../components/styledHelpComponents/styledHelpComponents';
import { latinToCyrillic } from 'src/helper/helperFunction';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import { exportToExcel, handlePrint } from './helper/exportHelper';
import { LoadingOverlay, mapStudents, studentColumns } from './helper/const';
import {
  fetchAllStudents,
  fetchCities,
  fetchSchoolsByCity,
  fetchStudentsAndCategories
} from './helper/apiConfig';
import ImportStudentsDialog from 'src/scenes/students/administration/helper/ImportStudentsDialog';

// Import the useDebounce hook
import { useDebounce } from './helper/useDebounce';

/* ADMIN STRANA - GLAVNA STRANA GDE SE VRSI ADMINSITRACIJA STUDENATA */
const AdminStudents = ({ schoolId }) => {
  const [students, setStudents] = useState([]);
  const [categories, setCategories] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [selectedTab, setSelectedTab] = useState('all');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [rowCount, setRowCount] = useState(0);
  const { t } = useTranslation();
  const localeText = useLocaleText();
  const { role } = useContext(RoleContext);
  const [totalStudents, setTotalStudents] = useState(0);
  const [schools, setSchools] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [allSchool, setAllSchool] = useState(null);
  const { activeModes } = useContext(ActiveModesContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Import dialog
  const [openImportDialog, setOpenImportDialog] = useState(false);

  // Logic for disabling edit/delete
  const isEditOrDeleteDisabled = useMemo(() => {
    if (role === 'ADMIN') return false;
    return activeModes.some((mode) =>
      [
        FLAG_ZALBE,
        FLAG_REZULTATI_SKOLA_G,
        FLAG_REZULTATI,
        FLAG_FINALE,
        FLAG_ZABRANA
      ].includes(mode)
    );
  }, [activeModes, role]);

  // Fetch function for students and categories
  const fetchStudentsData = useCallback(async () => {
    const controller = new AbortController();
    const signal = controller.signal;

    setIsLoading(true);
    try {
      const { students, total, categories } = await fetchStudentsAndCategories({
        pageSize,
        page,
        selectedTab,
        selectedSchool,
        selectedCity,
        searchQuery: debouncedSearchQuery,
        signal
      });
      setStudents(mapStudents(students));
      setCategories(categories);
      setRowCount(Number(total));
      setTotalStudents(total);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching students:', error);
        setError('Failed to load data');
      }
    } finally {
      setIsLoading(false);
    }

    return () => {
      controller.abort();
    };
  }, [
    pageSize,
    page,
    selectedTab,
    selectedSchool,
    selectedCity,
    debouncedSearchQuery
  ]);

  // Fetch cities on component mount
  useEffect(() => {
    const fetchCitiesData = async () => {
      const citiesData = await fetchCities();
      setCities(citiesData);
    };
    fetchCitiesData();
  }, []);

  // Fetch schools when selectedCity changes
  useEffect(() => {
    const fetchSchools = async () => {
      setSelectedSchool(null);
      if (selectedCity) {
        const schoolsData = await fetchSchoolsByCity(selectedCity.zipcode);
        setSchools(schoolsData);
      } else {
        setSchools([]); // Clear schools if no city is selected
      }
    };
    fetchSchools();
  }, [selectedCity]);

  // Fetch students data when dependencies change
  useEffect(() => {
    fetchStudentsData();
  }, [
    selectedTab,
    selectedSchool,
    selectedCity,
    page,
    pageSize,
    debouncedSearchQuery,
    fetchStudentsData
  ]);

  // Handle dialog toggle
  const handleDialogToggle = useCallback((open, student) => {
    setOpenDialog(open);
    if (open) {
      setSelectedStudent(student);
    } else {
      setSelectedStudent(null);
    }
  }, []);

  // Handle confirm delete dialog toggle
  const handleConfirmDeleteToggle = useCallback((open, student) => {
    setOpenConfirmDeleteDialog(open);
    if (open) setStudentToDelete(student);
  }, []);

  // Handle delete student
  const handleDeleteStudent = async () => {
    try {
      await axios.delete(
        `${API_BASE_URL}/api/students.php?id=${studentToDelete.id}`
      );
      setSnackbarMessage(t('alerts.studentDeleted'));
      setSnackbarOpen(true);
      fetchStudentsData();
    } catch (error) {
      console.error('Error deleting student:', error);
    } finally {
      handleConfirmDeleteToggle(false);
    }
  };

  // Handle add student success
  const handleAddStudentSuccess = () => {
    setSnackbarMessage(t('alerts.studentAdded'));
    setSnackbarOpen(true);
    fetchStudentsData();
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setPage(1);
  };

  // Handle search change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setPage(newPage + 1);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  // Filter options for Autocomplete (Cities)
  const filterCityOptions = (options, { inputValue }) => {
    const inputCyrillic = latinToCyrillic(inputValue);
    return options.filter((option) =>
      option.name.toLowerCase().includes(inputCyrillic.toLowerCase())
    );
  };

  // Filter options for Autocomplete (Schools)
  const filterSchoolOptions = (options, { inputValue }) => {
    const inputCyrillic = latinToCyrillic(inputValue);
    return options.filter((option) =>
      option.name.toLowerCase().includes(inputCyrillic.toLowerCase())
    );
  };

  // Handle export to Excel
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const allStudents = await fetchAllStudents({
        selectedTab,
        selectedSchool,
        selectedCity,
        searchQuery: debouncedSearchQuery
      });
      exportToExcel(allStudents, 'Učenici');
    } catch (error) {
      console.error('Error during export:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle print
  const handlePrintAllStudents = async () => {
    try {
      const allStudents = await fetchAllStudents({
        selectedTab,
        selectedSchool,
        selectedCity,
        searchQuery: debouncedSearchQuery
      });
      const selectedStudents = allStudents.slice(0, 50);
      handlePrint(selectedStudents, categories);
    } catch (error) {
      console.error('Error fetching all students for print:', error);
    }
  };

  // Define columns
  const columns = useMemo(
    () =>
      studentColumns(
        t,
        categories,
        handleDialogToggle,
        handleConfirmDeleteToggle
      ),
    [categories, t, handleDialogToggle, handleConfirmDeleteToggle]
  );

  if (error) {
    return (
      <Box m="20px">
        <Typography variant="h4">{t('meni.students')}</Typography>
        <Box m="40px 0 0 0">
          <p>{error}</p>
        </Box>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box
        m="20px"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setAllSchool(true);
              handleDialogToggle(true);
            }}
            sx={{
              marginBottom: '20px',
              backgroundColor: '#009d74',
              '&:hover': {
                backgroundColor: isEditOrDeleteDisabled ? '#cccccc' : '#1c75bc'
              },
              color: '#ffffff',
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: 'bold'
            }}
          >
            {t('meni.addNewStudent')}
          </Button>
          {/* New Import Button */}
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setOpenImportDialog(true);
            }}
            sx={{
              marginLeft: '10px',
              marginBottom: '20px',
              backgroundColor: '#ff2626e0',
              '&:hover': {
                backgroundColor: isEditOrDeleteDisabled ? '#cccccc' : '#1c75bc'
              },
              color: '#ffffff',
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: 'bold'
            }}
          >
            {t('meni.importstudents')}
          </Button>
        </Box>
        {/* Link za preuzimanje šablona, poravnat desno */}
        <Typography
          variant="body1"
          sx={{
            fontWeight: 'bold',
            color: '#333'
          }}
        >
          <a
            href="/kengur2/assets/prijavni_list.xlsx"
            download
            style={{
              color: '#1c75bc',
              fontSize: '12px'
            }}
            onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
            onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
          >
            {t('links.downloadTemplate')}
          </a>
        </Typography>
      </Box>

      <Typography
        variant="h5"
        sx={{
          marginBottom: '10px',
          fontWeight: 'bold',
          color: '#333',
          textAlign: 'left'
        }}
      >
        {`${t('headers.enrolledStudents')}  (${totalStudents} - ${
          selectedTab === 'all'
            ? t('headers.total')
            : t('headers.grade', { grade: selectedTab })
        })`}
      </Typography>

      <TabsWrapper>
        <StyledTabs
          value={selectedTab}
          onChange={handleTabChange}
          aria-label={t('results.tabsLabel')}
          sx={{
            backgroundColor: '#f5f7fa',
            borderRadius: '8px',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
            marginBottom: '20px'
          }}
        >
          <Tab label={t('results.allClasses')} value="all" />
          {[...Array(12).keys()].map((num) => (
            <Tab
              label={t('results.grade', { grade: num + 1 })}
              value={(num + 1).toString()}
              key={num}
              sx={{
                fontWeight:
                  selectedTab === (num + 1).toString() ? 'bold' : 'normal',
                backgroundColor:
                  selectedTab === (num + 1).toString() ? '#4facfe' : '#ffffff',
                color:
                  selectedTab === (num + 1).toString() ? '#ffffff' : '#333',
                '&:hover': {
                  backgroundColor:
                    selectedTab === (num + 1).toString() ? '#1c75bc' : '#f0f0f0'
                },
                borderRadius: '8px',
                transition: 'background-color 0.3s, color 0.3s, box-shadow 0.3s'
              }}
            />
          ))}
        </StyledTabs>
      </TabsWrapper>

      <Box
        sx={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          marginBottom: '20px'
        }}
      >
        <TextField
          variant="outlined"
          placeholder={t('search.placeholder')}
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{
            width: '1000px',
            backgroundColor: '#f5f7fa',
            borderRadius: '8px'
          }}
        />

        <Autocomplete
          options={cities}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) =>
            option.zipcode === value.zipcode
          }
          filterOptions={filterCityOptions}
          onChange={(event, newValue) => {
            setSelectedCity(newValue);
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.zipcode}>
              {option.name}
            </li>
          )}
          noOptionsText={t('pagination.noRowsLabel')}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('filter.city')}
              variant="outlined"
              sx={{
                width: '200px',
                backgroundColor: '#f0f8ff',
                borderRadius: '12px',
                '& .MuiOutlinedInput-root': {
                  fontSize: '16px',
                  fontWeight: 'bold',
                  borderRadius: '12px',
                  padding: '10px'
                },
                '& .MuiInputLabel-root': {
                  fontSize: '16px',
                  fontWeight: 'bold'
                },
                '& .MuiAutocomplete-input': {
                  color: '#1c75bc'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1c75bc'
                }
              }}
            />
          )}
        />

        <Autocomplete
          options={schools}
          getOptionLabel={(option) => option.name}
          filterOptions={filterSchoolOptions}
          value={selectedSchool}
          onChange={(event, newValue) => setSelectedSchool(newValue)}
          disabled={selectedCity == null}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          )}
          noOptionsText={t('pagination.noRowsLabel')}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('filter.school')}
              variant="outlined"
              sx={{
                width: '200px',
                backgroundColor: '#f0f8ff',
                borderRadius: '12px',
                '& .MuiOutlinedInput-root': {
                  fontSize: '16px',
                  fontWeight: 'bold',
                  borderRadius: '12px',
                  padding: '10px'
                },
                '& .MuiInputLabel-root': {
                  fontSize: '16px',
                  fontWeight: 'bold'
                },
                '& .MuiAutocomplete-input': {
                  color: '#1c75bc'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1c75bc'
                }
              }}
            />
          )}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={handleExport}
          sx={{
            backgroundColor: '#4facfe',
            '&:hover': {
              backgroundColor: '#1c75bc'
            },
            color: '#ffffff',
            borderRadius: '8px',
            padding: '8px 16px'
          }}
          disabled={isExporting}
        >
          {isExporting ? <CircularProgress size={20} /> : <DownloadIcon />}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handlePrintAllStudents}
          sx={{ color: '#ffffff' }}
        >
          <PrintIcon />
        </Button>
      </Box>
      <Box
        m="0px 0 0 0"
        height={schoolId ? '35vh' : '75vh'}
        sx={{ backgroundColor: '#ffffff' }}
      >
        <DataGrid
          rows={students}
          columns={columns}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          paginationMode="server"
          rowCount={rowCount}
          page={page - 1}
          disableColumnMenu
          onPageChange={handlePageChange}
          localeText={localeText}
          loading={isLoading}
          components={{
            LoadingOverlay: LoadingOverlay
          }}
          componentsProps={{
            pagination: {
              labelRowsPerPage: t('pagination.rowsPerPage'),
              labelDisplayedRows: ({ from, to, count }) =>
                `${from}-${to} ${t('pagination.of')} ${
                  count !== -1 ? count : `${t('pagination.moreThan')} ${to}`
                }`
            }
          }}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              padding: '10px',
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
            },
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
              padding: '12px',
              fontSize: '14px',
              color: '#333'
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#2596be',
              color: '#ffffff',
              borderBottom: 'none',
              borderRadius: '12px 12px 0 0'
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: '#1c75bc',
              color: '#ffffff',
              borderTop: 'none',
              borderRadius: '0 0 12px 12px'
            },
            '& .MuiCheckbox-root': {
              color: '#28a745 !important'
            },
            '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
              color: '#ffffff !important'
            }
          }}
        />
      </Box>

      <AddStudentDialog
        open={openDialog}
        handleClose={() => handleDialogToggle(false)}
        studentData={selectedStudent}
        onSuccess={handleAddStudentSuccess}
        allSchoolView={allSchool}
      />
      <ImportStudentsDialog
        open={openImportDialog}
        // id skole
        handleClose={() => setOpenImportDialog(false)}
        onSuccess={() => {
          fetchStudentsData();
          setSnackbarMessage(t('alerts.studentsImported'));
          setSnackbarOpen(true);
        }}
      />
      <ConfirmDeleteDialog
        open={openConfirmDeleteDialog}
        onClose={() => handleConfirmDeleteToggle(false)}
        onConfirm={handleDeleteStudent}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={10000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ borderRadius: '8px' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminStudents;
